// Bibliothek: Kopf mit Größe/Anzahl/Dauer, Gesundheitsbanner, Smart Views,
// Filter, Ordnerbaum, Tabelle (Liste/Kacheln/Kompakt), Mehrfachauswahl mit
// Aktionsleiste, rechte Detailspalte mit Song-Editor.

import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad } from "../../api";
import { Empty, Field, Modal, PageHead, Switch, go, useInstance, useMe } from "../../ui";
import { CategoryEditor, DeleteDialog } from "../categories/Categories";
import { SongEditor } from "./SongEditor";
import { Upload } from "./Upload";
import { TimingEditor, timingText } from "../../schedule";
import { ContainerEditor } from "./Container";


function fmtHours(ms: number) {
  const h = ms / 3600_000;
  return h >= 1 ? `${h.toLocaleString("de-DE", { maximumFractionDigits: 1 })} h` : fmtMs(ms);
}
function fmtBytes(b: number) {
  return b > 1e9 ? `${(b / 1e9).toLocaleString("de-DE", { maximumFractionDigits: 1 })} GB` : `${Math.round(b / 1e6)} MB`;
}

/** One preview player for the whole list. */
const preview = new Audio();
function PreviewButton({ id }: { id: string }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const stop = () => setOn(false);
    preview.addEventListener("pause", stop);
    return () => preview.removeEventListener("pause", stop);
  }, []);
  return (
    <button className="iconbtn" title="Vorhören" onClick={(e) => {
      e.stopPropagation();
      if (on) return preview.pause();
      preview.src = `/api/media/${id}/audio`;
      preview.play().then(() => setOn(true)).catch(() => toast("Wiedergabe nicht möglich", true));
    }}>{on ? "■" : "▶"}</button>
  );
}

function Cover({ s, size }: { s: any; size: number }) {
  return s.cover ? (
    <img src={`/api/media/${s.id}/cover`} alt="" loading="lazy" style={{ width: size, height: size, borderRadius: 6, objectFit: "cover", display: "block" }} />
  ) : (
    <div style={{ width: size, height: size, borderRadius: 6, background: "linear-gradient(135deg, var(--side-2), var(--accent))", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: size / 2.6 }}>
      {(s.artist || s.title || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function Chips({ cats }: { cats: any[] }) {
  return (
    <span className="row wrap" style={{ gap: 4 }}>
      {cats.map((c) => <span key={c.id} className="badge" style={{ background: `${c.color}22`, color: c.color }}>{c.name}</span>)}
    </span>
  );
}

function FolderTree({ folders, current, onPick, onChanged, canEdit }: { folders: any[]; current: string; onPick: (id: string) => void; onChanged: () => void; canEdit: boolean }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const children = (pid: string | null) => folders.filter((f) => (f.parent_id ?? null) === pid).sort((a, b) => a.name.localeCompare(b.name, "de"));
  const create = async (parent: string | null) => {
    const name = window.prompt("Name des neuen Ordners");
    if (!name) return;
    await api("/api/folders", { body: { name, parent_id: parent } });
    onChanged();
  };
  const menu = async (f: any) => {
    const choice = window.prompt(`Ordner „${f.name}“: 1 = umbenennen, 2 = Unterordner, 3 = in Kategorie umwandeln, 4 = löschen (Songs bleiben)`, "1");
    if (choice === "1") {
      const name = window.prompt("Neuer Name", f.name);
      if (name) await api(`/api/folders/${f.id}`, { method: "PUT", body: { name } });
    } else if (choice === "2") return create(f.id);
    else if (choice === "3") {
      const r = await api(`/api/folders/${f.id}/to-category`, { body: {} });
      toast(`Kategorie „${r.name}“: alle Songs des Ordners zugeordnet, neue folgen automatisch`);
    } else if (choice === "4") {
      if (window.confirm(`Ordner „${f.name}“ samt Unterordnern löschen? Die Songs bleiben in der Bibliothek.`)) await api(`/api/folders/${f.id}`, { method: "DELETE" });
    }
    onChanged();
  };
  const node = (f: any, depth: number): JSX.Element => {
    const kids = children(f.id);
    return (
      <div key={f.id}>
        <div className="row" style={{ paddingLeft: depth * 12, gap: 4 }} onContextMenu={(e) => { e.preventDefault(); if (canEdit) menu(f); }}>
          <button className="iconbtn" style={{ visibility: kids.length ? "visible" : "hidden", padding: 2 }} onClick={() => setOpen({ ...open, [f.id]: !open[f.id] })}>{open[f.id] ? "▾" : "▸"}</button>
          <a href="#" onClick={(e) => { e.preventDefault(); onPick(f.id); }} style={{ fontWeight: current === f.id ? 700 : 400, color: "var(--ink)", flex: 1 }}>
            📁 {f.name} <span className="muted small">{f.songs}</span>
          </a>
          {f.auto_category_id && <span className="badge info" title="Neue Songs landen automatisch in einer Kategorie">auto</span>}
        </div>
        {open[f.id] && kids.map((k) => node(k, depth + 1))}
      </div>
    );
  };
  return (
    <div className="card" style={{ padding: 10 }}>
      <div className="row" style={{ marginBottom: 6 }}>
        <b>Ordner</b>
        <span className="spacer" />
        {canEdit && <button className="btn small" onClick={() => create(null)}>+</button>}
      </div>
      <a href="#" onClick={(e) => { e.preventDefault(); onPick(""); }} style={{ fontWeight: current === "" ? 700 : 400, color: "var(--ink)" }}>Ganze Bibliothek</a>
      <div style={{ marginTop: 4 }}>{children(null).map((f) => node(f, 0))}</div>
      {!folders.length && <div className="muted small" style={{ marginTop: 6 }}>Noch keine Ordner. Ordner enthalten nur Verknüpfungen.</div>}
      {canEdit && folders.length > 0 && <div className="muted small" style={{ marginTop: 6 }}>Rechtsklick auf einen Ordner: umbenennen, Unterordner, in Kategorie umwandeln, löschen.</div>}
    </div>
  );
}

function BulkBar({ ids, cats, folders, onDone }: { ids: string[]; cats: any[]; folders: any[]; onDone: () => void }) {
  const [timing, setTiming] = useState(false);
  const { data: stations } = useLoad<any[]>("/api/stations");
  const run = async (action: string, value?: any, confirm?: string) => {
    if (confirm && !window.confirm(confirm)) return;
    try {
      await api("/api/media/bulk", { body: { ids, action, value } });
      toast(`${ids.length} Songs: erledigt`);
      onDone();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="card row wrap" style={{ position: "sticky", top: 0, zIndex: 5, padding: "8px 12px", background: "var(--side)", color: "#fff" }}>
      <b>{ids.length} ausgewählt</b>
      <button className="btn small" onClick={() => run("approve")}>Freigeben</button>
      <select style={{ width: 170 }} value="" onChange={(e) => e.target.value && run("add_category", e.target.value)}>
        <option value="">+ Kategorie …</option>
        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select style={{ width: 170 }} value="" onChange={(e) => e.target.value && run("remove_category", e.target.value)}>
        <option value="">− Kategorie …</option>
        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select style={{ width: 160 }} value="" onChange={(e) => e.target.value && run("link_folder", e.target.value)}>
        <option value="">→ Ordner …</option>
        {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <select style={{ width: 150 }} value="" onChange={(e) => {
        const [field, value] = e.target.value.split(":");
        if (field) run("set", { field, value: value === "" ? null : value });
      }}>
        <option value="">Codieren …</option>
        {[1, 2, 3, 4, 5].map((n) => <option key={`e${n}`} value={`energy:${n}`}>Energie {n}</option>)}
        {[1, 2, 3, 4, 5].map((n) => <option key={`m${n}`} value={`mood:${n}`}>Stimmung {n}</option>)}
        {[1, 2, 3, 4, 5].map((n) => <option key={`t${n}`} value={`tempo:${n}`}>Tempo {n}</option>)}
        <option value="kind:music">Art: Musik</option><option value="kind:jingle">Art: Jingle</option><option value="kind:imaging">Art: Imaging</option><option value="kind:ad">Art: Werbung</option>
      </select>
      <select style={{ width: 170 }} value="" onChange={(e) => e.target.value && run("owner", e.target.value)}>
        <option value="">Gehört zu …</option>
        <option value="shared">allen Sendern (gemeinsam)</option>
        {(stations ?? []).map((st) => <option key={st.id} value={st.id}>nur {st.name}</option>)}
      </select>
      <button className="btn small" onClick={() => setTiming(true)}>Sendezeiten …</button>
      {ids.length > 1 && <button className="btn small" onClick={() => run("version")}>Als Versionen verknüpfen</button>}
      <button className="btn small" onClick={() => run("analyze")}>Neu analysieren</button>
      <button className="btn small" title="MusicBrainz: bester Treffer füllt nur leere Felder (läuft im Hintergrund, 1 Song/Sekunde)" onClick={() => run("lookup")}>Alle nachschlagen</button>
      <span className="spacer" />
      <button className="btn small danger" onClick={() => run("delete", undefined, `${ids.length} Songs endgültig aus der Bibliothek löschen?`)}>Löschen</button>
      {timing && <TimingEditor title={`Sendezeiten für ${ids.length} Titel`} value={null} onClose={() => setTiming(false)} onSave={async (t) => { await run("timing", t); setTiming(false); }} />}
    </div>
  );
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const { data: s, reload } = useLoad("/api/library/settings");
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: fields, reload: reloadFields } = useLoad<any[]>("/api/custom-fields");
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  if (!s) return null;
  const put = async (body: any) => {
    await api("/api/library/settings", { method: "PUT", body });
    reload();
  };
  return (
    <Modal title="Bibliothek – Einstellungen" onClose={onClose}>
      <div className="form">
        <Field label="Freigabe-Pflicht" full hint="Neue Uploads gehen erst nach „Für Sendung freigeben“ on air.">
          <Switch checked={s.require_approval} onChange={(x) => put({ require_approval: x })} label={s.require_approval ? "an" : "aus"} />
        </Field>
        <Field label="Neue Uploads landen in Kategorie" full>
          <select value={s.upload_category ?? ""} onChange={(e) => put({ upload_category: e.target.value || null })}>
            <option value="">– keine –</option>
            {(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <h3 style={{ marginTop: 18 }}>Eigene Felder</h3>
      {(fields ?? []).map((f) => (
        <div key={f.id} className="row">
          {f.label} <span className="muted small">{f.type}</span>
          <span className="spacer" />
          <button className="btn small danger" onClick={async () => { await api(`/api/custom-fields/${f.id}`, { method: "DELETE" }); reloadFields(); }}>Löschen</button>
        </div>
      ))}
      <div className="row" style={{ marginTop: 8 }}>
        <input type="text" placeholder="Bezeichnung, z. B. Komponist" value={label} onChange={(e) => setLabel(e.target.value)} />
        <select style={{ width: 120 }} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="text">Text</option><option value="number">Zahl</option><option value="date">Datum</option><option value="bool">Ja/Nein</option>
        </select>
        <button className="btn" disabled={!label.trim()} onClick={async () => {
          try { await api("/api/custom-fields", { body: { label, type } }); setLabel(""); reloadFields(); } catch (e: any) { toast(e.message, true); }
        }}>Hinzufügen</button>
      </div>
    </Modal>
  );
}

/** Short sign of a station for the folder tree: "Radio B6 - Test" → "B6". */
const short = (name?: string) => (name ?? "?").replace(/^Radio\s+/i, "").replace(/\s*-\s*Test$/i, "").split(/\s+/)[0].slice(0, 4).toUpperCase();
const ROLE_NAMES: Record<string, string> = { dropper: "Dropper", jingle: "Jingle", sweeper: "Sweeper", id: "Station-ID", promo: "Promo", ad: "Spot" };

/** Which of this station's folders plays which role in the clocks (Verbund role slots), and what steps in when a role is empty. */
function RolesPanel({ canEdit, onChanged }: { canEdit: boolean; onChanged: () => void }) {
  const { data, reload } = useLoad<any>("/api/roles");
  const [newRole, setNewRole] = useState("");
  if (!data) return <div className="card">Lade …</div>;
  const name = (k: string) => data.labels?.[k] ?? ROLE_NAMES[k] ?? k;
  const addRole = async () => {
    try { await api("/api/roles/defs", { body: { label: newRole } }); setNewRole(""); toast(`Rolle „${newRole}“ angelegt – jetzt in Uhren als Platz wählbar`); reload(); } catch (e: any) { toast(e.message, true); }
  };
  const setRole = async (catId: string, role: string | null) => {
    try { await api(`/api/categories/${catId}`, { method: "PUT", body: { role: role ?? "" } }); reload(); onChanged(); } catch (e: any) { toast(e.message, true); }
  };
  const setFallback = async (role: string, list: string[]) => {
    const all = Object.fromEntries(data.roles.map((r: any) => [r.role, r.role === role ? list : r.fallback]));
    try { await api("/api/roles/fallback", { method: "PUT", body: all }); toast("Ersatz gespeichert – der Sendeplan wird neu geplant"); reload(); } catch (e: any) { toast(e.message, true); }
  };
  return (
    <div className="card">
      <p className="small muted" style={{ marginTop: 0 }}>Die Uhren (auch die des Verbunds) haben Rollen-Plätze. Hier legst du fest, welcher Ordner dieses Senders die Rolle spielt. Fehlt eine Rolle, springt der Ersatz ein; erst wenn auch der leer ist, wird der Platz übersprungen. Dropper liegen immer auf der Rampe des nächsten Songs.</p>
      <table className="list">
        <thead><tr><th>Rolle</th><th>Ordner dieses Senders</th><th style={{ textAlign: "right" }}>Clips</th><th>Ersatz, wenn leer</th></tr></thead>
        <tbody>
          {data.roles.map((r: any) => {
            const clips = r.categories.reduce((a: number, c: any) => a + c.clips, 0);
            return (
              <tr key={r.role}>
                <td><b>{r.label ?? name(r.role)}</b>{r.used && <span className="badge info" style={{ marginLeft: 6 }} title="Kommt in den Uhren vor">in Uhren</span>}</td>
                <td>
                  {r.categories.map((c: any) => <span key={c.id} className="badge" style={{ marginRight: 4 }}>{c.name}{canEdit && <a href="#" style={{ marginLeft: 4 }} title="Rolle entfernen" onClick={(e) => { e.preventDefault(); setRole(c.id, null); }}>×</a>}</span>)}
                  {canEdit && (
                    <select style={{ width: 170 }} value="" onChange={(e) => e.target.value && setRole(e.target.value, r.role)}>
                      <option value="">+ Ordner zuordnen …</option>
                      {data.categories.filter((c: any) => c.role !== r.role).map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.role ? ` (jetzt ${name(c.role)})` : ""}</option>)}
                    </select>
                  )}
                </td>
                <td style={{ textAlign: "right" }} className={clips ? "" : "muted"}>{clips || (r.used ? <span className="badge warn">leer</span> : "–")}</td>
                <td>
                  <span className="small">{r.fallback.length ? r.fallback.map((x: string) => name(x)).join(" → ") : <span className="muted">keiner – überspringen</span>}</span>
                  {canEdit && (
                    <select style={{ width: 130, marginLeft: 6 }} value="" onChange={(e) => {
                      const v = e.target.value; if (!v) return;
                      setFallback(r.role, v === "-" ? [] : [...r.fallback.filter((x: string) => x !== v), v]);
                    }}>
                      <option value="">ändern …</option>
                      <option value="-">keinen Ersatz</option>
                      {data.roles.map((x: any) => x.role).filter((x: string) => x !== r.role && !r.fallback.includes(x)).map((x: string) => <option key={x} value={x}>+ {name(x)}</option>)}
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && (
        <div className="row" style={{ marginTop: 10 }}>
          <input type="text" style={{ maxWidth: 240 }} placeholder="Neue Rolle, z. B. Stundenopener" value={newRole} onChange={(e) => setNewRole(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newRole.trim() && addRole()} />
          <button className="btn" disabled={!newRole.trim()} onClick={addRole}>Rolle anlegen</button>
          <span className="small muted">In der Uhr wählst du die Rolle als Platz; jeder Sender ordnet ihr hier seinen Ordner zu.</span>
        </div>
      )}
    </div>
  );
}

type Sel = { t: "all" } | { t: "cat"; id: string } | { t: "view"; view: string } | { t: "folder"; id: string };
const selKey = (x: Sel) => (x.t === "cat" ? `cat:${x.id}` : x.t === "view" ? `view:${x.view}` : x.t === "folder" ? `folder:${x.id}` : "all");
const parseSel = (parts: string[]): Sel => {
  const [a, b] = parts;
  if (a === "ordner" && b) return { t: "cat", id: b };
  if (a === "ansicht" && b) return { t: "view", view: b };
  if (a === "dateiordner" && b) return { t: "folder", id: b };
  return { t: "all" };
};
const selPath = (x: Sel) => (x.t === "cat" ? `bibliothek/ordner/${x.id}` : x.t === "view" ? `bibliothek/ansicht/${x.view}` : x.t === "folder" ? `bibliothek/dateiordner/${x.id}` : "bibliothek");
const PACK_KIND: Record<string, string> = { dropper: "dropper", jingle: "jingle", sweeper: "imaging", id: "station_id", promo: "imaging", ad: "ad" };
const CHECKS: [string, string][] = [["pending", "Wartet auf Freigabe"], ["no_artist_title", "Interpret/Titel fehlt"], ["no_outro", "Kein Outro-Cue"], ["no_meta", "Metadaten fehlen"], ["no_cover", "Kein Cover"], ["errors", "Fehler"], ["restricted", "Mit Sendezeiten"]];

/** Library and rotation in one (like mAirList): the categories are the folders; clicking one shows its titles,
 *  its rotation settings sit on the folder. Drag titles onto another folder to move them (Alt/⌘: also add). */
export function LibraryPage({ parts = [] }: { parts?: string[] }) {
  const me = useMe();
  const inst = useInstance();
  const canEdit = ["admin", "programm", "redaktion"].includes(me?.role);
  const canRotation = ["admin", "programm"].includes(me?.role);
  const rotationTab = parts[0] === "rotation";
  const sel = parseSel(rotationTab ? [] : parts);
  const [tab, setTab] = useState<"titel" | "rotation">(rotationTab ? "rotation" : "titel");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [sort, setSort] = useState("artist");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [detail, setDetail] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ files?: File[] } | null>(null);
  const [container, setContainer] = useState<string | null>(null);
  const [settings, setSettings] = useState(false);
  const [newCat, setNewCat] = useState<any | null>(null);
  const [delCat, setDelCat] = useState<any | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({ music: true, pack: true });
  const [dropOn, setDropOn] = useState<string | null>(null);
  const [fileOver, setFileOver] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const goSel = (x: Sel) => { go(selPath(x)); setPage(1); setSelected([]); setDetail(null); setTab("titel"); };

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { setQDebounced(q); setPage(1); }, 300);
  }, [q]);

  const params = useMemo(() => {
    const p = new URLSearchParams({ sort, page: String(page), view: sel.t === "view" ? sel.view : "all" });
    if (qDebounced) p.set("q", qDebounced);
    if (sel.t === "cat") p.set("category", sel.id);
    if (sel.t === "folder") p.set("folder", sel.id);
    return p.toString();
  }, [sort, page, qDebounced, selKey(sel)]);

  const { data, reload } = useLoad<any>(`/api/library?${params}`, [params]);
  const { data: health, reload: reloadHealth } = useLoad<any>("/api/library/health");
  const { data: cats, reload: reloadCats } = useLoad<any[]>("/api/categories");
  const { data: groups } = useLoad<any[]>("/api/category-groups");
  const { data: folders, reload: reloadFolders } = useLoad<any[]>("/api/folders");
  const refresh = () => { reload(); reloadHealth(); reloadCats(); reloadFolders(); };

  useEffect(() => {
    if (!health?.queued && !health?.analyzing) return;
    const t = window.setTimeout(refresh, 4000);
    return () => window.clearTimeout(t);
  }, [health]);

  const all = cats ?? [];
  const music = all.filter((c) => c.kind === "music" && !c.station_id);
  const own = all.filter((c) => c.station_id && c.station_id === inst?.id);
  const cur = sel.t === "cat" ? all.find((c) => c.id === sel.id) : null;
  const isPack = !!cur?.station_id || (sel.t === "view" && ["own", "containers"].includes(sel.view));
  const items: any[] = data?.items ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const allSel = items.length > 0 && items.every((i) => selected.includes(i.id));
  const toggle = (id: string) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const verbund = inst?.kind === "verbund";
  const musicLabel = verbund ? "Musik (Verbund)" : "Musik (Verbund)";
  const packLabel = `Eigene Ordner ${inst?.name ?? ""}`;

  /** Titles dropped on a folder: move there (from the open folder), with Alt/⌘ also add. */
  const dropTitles = async (to: any, e: React.DragEvent) => {
    e.preventDefault();
    setDropOn(null);
    const ids = JSON.parse(e.dataTransfer.getData("application/x-rc-titles") || "[]");
    if (!ids.length || !canEdit) return;
    const copy = e.altKey || e.metaKey || e.ctrlKey || sel.t !== "cat";
    try {
      await api("/api/media/bulk", { body: { ids, action: "move_category", value: { to: to.id, from: copy ? null : (sel as any).id } } });
      toast(`${ids.length} ${ids.length === 1 ? "Titel" : "Titel"} ${copy ? "zusätzlich in" : "verschoben nach"} „${to.name}“`);
      setSelected([]);
      refresh();
    } catch (x: any) { toast(x.message, true); }
  };
  const dragTitles = (id: string) => (e: React.DragEvent) => {
    const ids = selected.includes(id) ? selected : [id];
    e.dataTransfer.setData("application/x-rc-titles", JSON.stringify(ids));
    e.dataTransfer.effectAllowed = "copyMove";
  };

  const total = (list: any[]) => list.reduce((a, c) => a + Number(c.total_ms ?? 0), 0);
  const count = (list: any[]) => list.reduce((a, c) => a + Number(c.songs ?? 0), 0);
  const catNode = (c: any, depth: number) => (
    <div key={c.id}
      className={`lt-node${sel.t === "cat" && sel.id === c.id ? " cur" : ""}${dropOn === c.id ? " drop" : ""}`}
      style={{ paddingLeft: 10 + depth * 14 }}
      onClick={() => goSel({ t: "cat", id: c.id })}
      onDragOver={(e) => { if (e.dataTransfer.types.includes("application/x-rc-titles")) { e.preventDefault(); setDropOn(c.id); } }}
      onDragLeave={() => setDropOn(null)}
      onDrop={(e) => dropTitles(c, e)}
      title={`${c.songs} Titel · ${fmtHours(Number(c.total_ms ?? 0))}${c.target_pct ? ` · Ziel ${c.target_pct} %` : ""}`}>
      <i className="lt-dot" style={{ background: c.color }} />
      <span className="lt-name">{c.name}</span>
      {!verbund && <span className="lt-own" title={c.station_id ? `gehört nur ${inst?.name}` : "Verbund – gilt für alle Sender"} style={{ background: c.station_id ? inst?.color : "#5b4bb7" }}>{c.station_id ? short(inst?.name) : "V"}</span>}
      {(c.schedule || c.valid_from || c.valid_to) && <span title="Sendezeiten">🕑</span>}
      {c.role && <span className="lt-tag">{({ dropper: "Dropper", jingle: "Jingle", sweeper: "Sweeper", id: "ID", promo: "Promo", ad: "Spot" } as any)[c.role]}</span>}
      <span className="lt-n">{c.songs}</span>
    </div>
  );
  const root = (key: string, label: string, list: any[], extra?: JSX.Element | null, sub?: string) => (
    <div className="lt-root">
      <div className="lt-head" onClick={() => setOpen({ ...open, [key]: !open[key] })}>
        <span className="lt-caret">{open[key] ? "▾" : "▸"}</span>
        <b>{label}</b>
        <span className="lt-n">{count(list)} · {fmtHours(total(list))}</span>
      </div>
      {sub && open[key] && <div className="small muted" style={{ padding: "0 12px 4px 28px" }}>{sub}</div>}
      {open[key] && extra}
    </div>
  );
  const grouped = (list: any[]) => {
    const gs = (groups ?? []).filter((g) => list.some((c) => c.group_id === g.id));
    return (
      <>
        {gs.map((g) => (
          <div key={g.id}>
            <div className="lt-node lt-group" style={{ paddingLeft: 10 }} onClick={() => setOpen({ ...open, [g.id]: !open[g.id] })}>
              <span className="lt-caret">{open[g.id] === false ? "▸" : "▾"}</span><span className="lt-name">{g.name}</span>
              <span className="lt-n">{count(list.filter((c) => c.group_id === g.id))}</span>
            </div>
            {open[g.id] !== false && list.filter((c) => c.group_id === g.id).map((c) => catNode(c, 1))}
          </div>
        ))}
        {list.filter((c) => !c.group_id || !gs.some((g) => g.id === c.group_id)).map((c) => catNode(c, 0))}
      </>
    );
  };
  const viewNode = (view: string, label: string, n?: number, depth = 0) => (
    <div key={view} className={`lt-node${sel.t === "view" && sel.view === view ? " cur" : ""}`} style={{ paddingLeft: 10 + depth * 14 }} onClick={() => goSel({ t: "view", view })}>
      <span className="lt-name">{label}</span>{n != null && n > 0 && <span className="lt-n">{n}</span>}
    </div>
  );

  const crumb = sel.t === "cat" && cur ? [cur.station_id ? packLabel : musicLabel, ...(cur.group_name ? [cur.group_name] : []), cur.name]
    : sel.t === "view" ? [({ unsorted: musicLabel, own: packLabel, containers: packLabel, rollen: packLabel } as any)[sel.view] ?? "Prüfen", ({ unsorted: "Unsortierte Musik", own: "Alle Clips", containers: "Container", rollen: "Rollen-Zuordnung" } as any)[sel.view] ?? CHECKS.find(([k]) => k === sel.view)?.[1] ?? sel.view]
    : sel.t === "folder" ? ["Dateiordner", (folders ?? []).find((f) => f.id === sel.id)?.name ?? ""] : ["Ganze Bibliothek"];
  const heading = crumb[crumb.length - 1];
  const uploadPreset = cur ? { categoryId: cur.id, kind: cur.kind === "music" ? "music" : cur.kind, owner: cur.station_id ? "station" : "shared" }
    : isPack ? { kind: "jingle", owner: "station" } : { kind: "music", owner: "shared" };
  const problems = health ? CHECKS.filter(([k]) => health[k] > 0 && k !== "restricted") : [];

  return (
    <div>
      <PageHead title="Bibliothek & Rotation" sub={health ? `${health.all} Titel · ${fmtHours(health.total_ms)} · ${fmtBytes(health.total_bytes)}${health.queued ? ` · ${health.queued} in Analyse` : ""}` : "…"}>
        {canEdit && <button className="btn" onClick={() => setSettings(true)}>Einstellungen</button>}
        <a className="btn" href="#/plan/kategorien/board">Rotationsboard</a>
        <a className="btn" href="#/plan/kategorien/analyse">Analyse</a>
      </PageHead>
      {problems.length > 0 && (
        <div className="banner warn">⚠ <span>{problems.map(([k, l]) => `${health[k]} × ${l}`).join(" · ")}</span><span className="spacer" />
          <a href="#" onClick={(e) => { e.preventDefault(); goSel({ t: "view", view: problems[0][0] }); }}>Anzeigen →</a></div>
      )}
      <div className="lib">
        <aside className="lib-tree card">
          <div className={`lt-node${sel.t === "all" ? " cur" : ""}`} onClick={() => goSel({ t: "all" })}><b className="lt-name">Ganze Bibliothek</b><span className="lt-n">{health?.all ?? ""}</span></div>
          {root("music", musicLabel, music, (
            <>
              {grouped(music)}
              {viewNode("unsorted", "Unsortierte Musik", health?.unsorted)}
              {canRotation && <div className="lt-add" onClick={() => setNewCat({ kind: "music" })}>+ Ordner (Kategorie)</div>}
            </>
          ), !verbund && inst ? "Läuft, wenn dieser Sender nicht vom Verbund übernimmt." : undefined)}
          {!verbund && root("pack", packLabel, own, (
            <>
              {grouped(own)}
              {viewNode("containers", "Container", health?.containers)}
              {viewNode("own", "Alle Clips dieses Senders", health?.own)}
              {viewNode("rollen", "Rollen-Zuordnung")}
              {canEdit && <div className="lt-add" onClick={() => setNewCat({ kind: "jingle", role: "jingle", owner: "station", station_id: inst?.id })}>+ Ordner dieses Senders (Jingles, Dropper, Stundenopener …)</div>}
            </>
          ), own.length ? "Verpackung und eigene Ordner wie Stundenopener – andere Sender sehen sie nicht." : "Noch leer – Ordner anlegen und Clips hineinziehen oder hochladen.")}
          {verbund && <div className="small muted" style={{ padding: "8px 12px" }}>Der Verbund hat keine Verpackung: Die Uhren haben Rollen-Plätze (Jingle, Dropper …), die jeder Sender mit seinen Clips füllt.</div>}
          {root("check", "Prüfen", [], <>{CHECKS.map(([k, l]) => viewNode(k, l, health?.[k]))}</>)}
          {(folders ?? []).length > 0 && root("files", "Dateiordner", [], <FolderTree folders={folders ?? []} current={sel.t === "folder" ? sel.id : ""} onPick={(id) => goSel(id ? { t: "folder", id } : { t: "all" })} onChanged={refresh} canEdit={canEdit} />)}
        </aside>

        <section className="lib-main"
          onDragOver={(e) => { if (canEdit && e.dataTransfer.types.includes("Files")) { e.preventDefault(); setFileOver(true); } }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setFileOver(false); }}
          onDrop={(e) => { if (!e.dataTransfer.files.length) return; e.preventDefault(); setFileOver(false); setUpload({ files: Array.from(e.dataTransfer.files) }); }}>
          <div className="lib-head">
            <div style={{ minWidth: 0 }}>
              <div className="small muted">{crumb.slice(0, -1).join(" › ")}</div>
              <h2 style={{ margin: 0 }}>{cur && <i className="lt-dot big" style={{ background: cur.color }} />}{heading}</h2>
              <div className="small muted">{data ? `${data.total} Titel` : "…"}{cur ? ` · ${fmtHours(Number(cur.total_ms ?? 0))}${cur.target_pct ? ` · Ziel ${cur.target_pct} %` : ""}${cur.plays_today ? ` · heute ${cur.plays_today}× gespielt` : ""}` : ""}</div>
            </div>
            <span className="spacer" />
            {cur && <div className="seg"><button className={tab === "titel" ? "on" : ""} onClick={() => setTab("titel")}>Titel</button><button className={tab === "rotation" ? "on" : ""} onClick={() => setTab("rotation")}>Rotation</button></div>}
            {canEdit && isPack && <button className="btn" onClick={() => setContainer("new")}>+ Container</button>}
            {canEdit && <button className="btn primary" onClick={() => setUpload({})}>{cur ? `In „${cur.name}“ hochladen` : isPack ? "Verpackung hochladen" : "Hochladen"}</button>}
          </div>

          {sel.t === "view" && sel.view === "rollen" ? (
            <RolesPanel canEdit={canRotation} onChanged={refresh} />
          ) : cur && tab === "rotation" ? (
            <div className="stack">
              {canRotation ? <CategoryEditor key={cur.id} inline row={cur} groups={groups ?? []} onClose={(c) => c && refresh()} /> : <Empty>Rotations-Einstellungen ändern: Rolle Admin oder Programm.</Empty>}
              {canRotation && <div className="row"><span className="spacer" /><button className="btn danger" onClick={() => setDelCat(cur)}>Ordner „{cur.name}“ löschen …</button></div>}
            </div>
          ) : (
            <>
              <div className="row wrap" style={{ margin: "10px 0" }}>
                <input type="search" style={{ maxWidth: 320 }} placeholder={`Suchen in „${heading}“ …`} value={q} onChange={(e) => setQ(e.target.value)} />
                <select style={{ width: 160 }} value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="artist">Interpret A–Z</option><option value="title">Titel A–Z</option><option value="added">Neueste zuerst</option>
                  <option value="duration">Längste zuerst</option><option value="year">Jahr</option>
                </select>
                <span className="spacer" />
                <span className="small muted">{canEdit ? "Titel auf einen Ordner links ziehen = verschieben · mit Alt/⌘ = zusätzlich zuordnen · Dateien hierher ziehen = hochladen" : ""}</span>
              </div>
              {selected.length > 0 && canEdit && <BulkBar ids={selected} cats={all} folders={folders ?? []} onDone={() => { setSelected([]); refresh(); }} />}
              <div className={`card lib-table${fileOver ? " over" : ""}`} style={{ padding: 0 }}>
                {fileOver && <div className="lib-drop">Loslassen zum Hochladen {cur ? `in „${cur.name}“` : ""}</div>}
                {!items.length ? (
                  <Empty>{data ? (sel.t === "cat" ? "Dieser Ordner ist leer. Titel aus einem anderen Ordner hierher ziehen oder Dateien hochladen." : "Keine Titel in dieser Ansicht.") : "Lade …"}</Empty>
                ) : (
                  <table className="list">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}><input type="checkbox" checked={allSel} onChange={() => setSelected(allSel ? [] : items.map((i) => i.id))} /></th>
                        <th style={{ width: 30 }}></th>
                        <th>Titel / Interpret</th>
                        <th style={{ textAlign: "right", width: 64 }}>Länge</th>
                        <th style={{ textAlign: "right", width: 56 }}>Intro</th>
                        <th style={{ width: 70 }}>BPM · E</th>
                        <th>Ordner</th>
                        <th style={{ width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((s) => (
                        <tr key={s.id} className="click" draggable={canEdit} onDragStart={dragTitles(s.id)} onClick={() => setDetail(s.id)}
                          style={{ background: detail === s.id ? "var(--accent-2)" : selected.includes(s.id) ? "var(--paper)" : undefined }}>
                          <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} /></td>
                          <td onClick={(e) => e.stopPropagation()}><PreviewButton id={s.id} /></td>
                          <td style={{ minWidth: 0 }}>
                            <div className="row" style={{ gap: 10 }}>
                              <Cover s={s} size={32} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || <span className="muted">(ohne Titel)</span>} {!s.released && <span className="badge warn">Freigabe</span>}</div>
                                <div className="small muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.artist || "–"}{s.year ? ` · ${s.year}` : ""}{s.kind !== "music" ? ` · ${s.kind}` : ""}</div>
                              </div>
                            </div>
                          </td>
                          <td className="small mono" style={{ textAlign: "right" }}>{fmtMs(s.duration_ms)}</td>
                          <td className="small mono muted" style={{ textAlign: "right" }}>{s.intro != null ? `${Math.round((s.intro - (s.cue_in ?? 0)) / 1000)} s` : "–"}</td>
                          <td className="small muted">{s.bpm ? Math.round(s.bpm) : "–"}{s.energy ? ` · ${s.energy}` : ""}</td>
                          <td><Chips cats={(s.categories ?? []).filter((c: any) => !(sel.t === "cat" && c.id === sel.id))} /></td>
                          <td>{(s.schedule || s.valid_from || s.valid_to) ? <span title={`Sendezeiten: ${timingText(s)}`}>🕑</span> : s.status === "analyzing" ? <span className="badge info">…</span> : s.warn ? <span className="badge warn" title={s.error ?? "Daten unvollständig oder kein Outro-Cue"}>!</span> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {pages > 1 && (
                <div className="row" style={{ marginTop: 8 }}>
                  <button className="btn small" disabled={page <= 1} onClick={() => setPage(page - 1)}>← zurück</button>
                  <span className="small muted">Seite {page} von {pages} · {data.total} Titel</span>
                  <button className="btn small" disabled={page >= pages} onClick={() => setPage(page + 1)}>weiter →</button>
                </div>
              )}
            </>
          )}
        </section>
        {detail && <aside className="lib-detail"><SongEditor id={detail} onClose={() => setDetail(null)} onChanged={refresh} /></aside>}
      </div>
      {container && <ContainerEditor id={container === "new" ? null : container} onClose={(c) => { setContainer(null); if (c) refresh(); }} />}
      {upload && <Upload title={cur ? `Hochladen in „${cur.name}“` : isPack ? `Verpackung für ${inst?.name ?? "diesen Sender"} hochladen` : undefined} files={upload.files} folderId={sel.t === "folder" ? sel.id : null} {...uploadPreset} onClose={(changed) => { setUpload(null); if (changed) refresh(); }} />}
      {settings && <SettingsModal onClose={() => { setSettings(false); refresh(); }} />}
      {newCat && <CategoryEditor row={null} groups={groups ?? []} defaults={newCat} onClose={(c) => { setNewCat(null); if (c) refresh(); }} />}
      {delCat && <DeleteDialog cat={delCat} cats={all} onClose={(c) => { setDelCat(null); if (c) { refresh(); goSel({ t: "all" }); } }} />}
    </div>
  );
}
